import React, { useState, useEffect } from 'react';
import { X, Plus, Pencil, Trash2, CreditCard, Star } from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { useToast } from '../../../hooks/use-toast';
import BankAccountFormModal from './BankAccountFormModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BankAccountsManager = ({ isOpen, onClose, company, onSuccess }) => {
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const { toast } = useToast();

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (isOpen && company) {
      fetchBankAccounts();
    }
  }, [isOpen, company]);

  const fetchBankAccounts = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}/api/procurement/bank-accounts?company_id=${company.id}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch bank accounts');
      const data = await response.json();
      setBankAccounts(data);
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
      toast({
        title: 'Error',
        description: 'Failed to load bank accounts',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (account) => {
    setSelectedAccount(account);
    setIsFormModalOpen(true);
  };

  const handleDelete = async (accountId) => {
    if (!window.confirm('Are you sure you want to delete this bank account?')) return;

    try {
      const response = await fetch(
        `${API_URL}/api/procurement/bank-accounts/${accountId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete bank account');
      }

      toast({
        title: 'Success',
        description: 'Bank account deleted successfully'
      });
      fetchBankAccounts();
      onSuccess();
    } catch (error) {
      console.error('Error deleting bank account:', error);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const handleFormSuccess = () => {
    fetchBankAccounts();
    setIsFormModalOpen(false);
    setSelectedAccount(null);
    onSuccess();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-[#2B2D42]">Bank Accounts</h2>
            <p className="text-sm text-[#6C757D]">{company?.legal_name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-[#6C757D] hover:text-[#2B2D42] transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Add Account Button */}
          <div className="mb-6">
            <Button
              onClick={() => {
                setSelectedAccount(null);
                setIsFormModalOpen(true);
              }}
              className="bg-[#00B4D8] hover:bg-[#0096B8]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Bank Account
            </Button>
          </div>

          {/* Bank Accounts List */}
          {loading ? (
            <p className="text-center text-[#6C757D] py-8">Loading bank accounts...</p>
          ) : bankAccounts.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
              <CreditCard className="h-12 w-12 mx-auto text-[#6C757D] mb-3" />
              <p className="text-[#6C757D]">No bank accounts yet.</p>
              <p className="text-sm text-[#6C757D]">Add your first bank account to get started.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {bankAccounts.map((account) => (
                <div
                  key={account.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-[#00B4D8] transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <CreditCard className="h-5 w-5 text-[#00B4D8]" />
                        <h3 className="text-lg font-semibold text-[#2B2D42]">
                          {account.bank_name}
                        </h3>
                        {account.is_default && (
                          <Badge className="bg-green-100 text-green-800">
                            <Star className="h-3 w-3 mr-1" />
                            Default
                          </Badge>
                        )}
                        <Badge className="bg-blue-100 text-blue-800">
                          {account.currency}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs text-[#6C757D] mb-1">Account Name</p>
                          <p className="text-sm text-[#2B2D42] font-medium">
                            {account.account_name}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-[#6C757D] mb-1">Account Number</p>
                          <p className="text-sm text-[#2B2D42] font-mono">
                            {account.account_number}
                          </p>
                        </div>
                        {account.iban && (
                          <div>
                            <p className="text-xs text-[#6C757D] mb-1">IBAN</p>
                            <p className="text-sm text-[#2B2D42] font-mono">
                              {account.iban}
                            </p>
                          </div>
                        )}
                        {account.swift && (
                          <div>
                            <p className="text-xs text-[#6C757D] mb-1">SWIFT/BIC</p>
                            <p className="text-sm text-[#2B2D42] font-mono">
                              {account.swift}
                            </p>
                          </div>
                        )}
                        {account.routing && (
                          <div>
                            <p className="text-xs text-[#6C757D] mb-1">Routing</p>
                            <p className="text-sm text-[#2B2D42] font-mono">
                              {account.routing}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col space-y-2 ml-4">
                      <Button
                        onClick={() => handleEdit(account)}
                        variant="outline"
                        size="sm"
                        className="border-[#00B4D8] text-[#00B4D8] hover:bg-[#00B4D8] hover:text-white"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {!account.is_default && (
                        <Button
                          onClick={() => handleDelete(account.id)}
                          variant="outline"
                          size="sm"
                          className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Close Button */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </div>
      </div>

      {/* Bank Account Form Modal */}
      <BankAccountFormModal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setSelectedAccount(null);
        }}
        companyId={company?.id}
        account={selectedAccount}
        onSuccess={handleFormSuccess}
      />
    </div>
  );
};

export default BankAccountsManager;
